import { useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';

import { apiService } from '../services/api';
import { getStaticData } from '../hooks/useStaticData';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const LocationSection = () => {
  const [businessInfo, setBusinessInfo] = useState(() => getStaticData('businessInfo'));
  const [schedule, setSchedule] = useState(() => {
    const staticSchedule = getStaticData('schedule');
    return staticSchedule ? (Array.isArray(staticSchedule) ? staticSchedule : Object.values(staticSchedule)) : [];
  });
  const [loading, setLoading] = useState(() => !getStaticData('businessInfo'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [businessData, scheduleData] = await Promise.all([
          apiService.getBusinessInfo(),
          apiService.getSchedule()
        ]);
        if (businessData) setBusinessInfo(businessData);
        if (scheduleData) setSchedule(scheduleData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDirections = () => {
    if (!businessInfo) return;
    const address = encodeURIComponent(businessInfo.address);
    window.open(`https://www.google.com/maps/search/${address}`, '_blank');
  };

  const handleCall = () => {
    if (!businessInfo) return;
    window.location.href = `tel:${businessInfo.phone}`;
  };

  const getMapEmbedUrl = () => {
    if (!businessInfo) return '';
    const encodedAddress = encodeURIComponent(businessInfo.address);
    // Use standard Google Maps embed without API key to avoid 403 errors
    return `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const hourNum = parseInt(hour, 10);
    const hour12 = hourNum === 0 ? 12 : (hourNum > 12 ? hourNum - 12 : hourNum);
    const ampm = hourNum >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minute} ${ampm}`;
  };

  // A "placeholder" day is one whose only time slot covers the full open
  // window (8a-8p) with no linked program — used to mark days that operate
  // by appointment / invitation / private rather than a real class block.
  // For those days we show the slot's `note` (e.g. "By Invitation", "Private")
  // instead of a 12-hour time range, which is more honest about what the
  // day actually is.
  const isPlaceholderDay = (slots) =>
    slots.length === 1 &&
    !slots[0].program_id &&
    slots[0].note &&
    slots[0].start_time === '08:00' &&
    slots[0].end_time === '20:00';

  const getTrainingHours = () => {
    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const hours = [];

    daysOfWeek.forEach((day, index) => {
      const daySchedule = schedule.find(s => s.day === day);
      if (daySchedule && daySchedule.time_slots && daySchedule.time_slots.length > 0) {
        // Sort time slots by start time
        const sortedSlots = [...daySchedule.time_slots].sort((a, b) => {
          return a.start_time.localeCompare(b.start_time);
        });

        if (isPlaceholderDay(sortedSlots)) {
          hours.push({
            day: dayLabels[index],
            time: sortedSlots[0].note,
          });
          return;
        }

        const firstSlot = sortedSlots[0];
        const lastSlot = sortedSlots[sortedSlots.length - 1];
        hours.push({
          day: dayLabels[index],
          time: `${formatTime(firstSlot.start_time)} - ${formatTime(lastSlot.end_time)}`
        });
      } else {
        hours.push({
          day: dayLabels[index],
          time: 'Closed'
        });
      }
    });

    return hours;
  };

  if (loading || !businessInfo) {
    return <div>Loading...</div>;
  }

  const trainingHours = getTrainingHours();

  return (
    <section id="location" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Visit Our Studio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Located in the heart of Spring Hill, FL. Easy access with convenient parking 
            for all families.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Dynamic Google Map */}
          <div className="relative">
            <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden shadow-lg">
              <iframe
                src={getMapEmbedUrl()}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Owaza Martial Arts Location"
              ></iframe>
            </div>
            <Button 
              className="absolute bottom-4 right-4 bg-red-600 hover:bg-red-700 text-white shadow-lg"
              onClick={handleDirections}
            >
              <Navigation className="h-4 w-4 mr-2" />
              Get Directions
            </Button>
          </div>

          {/* Contact Info Cards */}
          <div className="space-y-6">
            {/* Address */}
            <Card className="shadow-lg border-0">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center space-x-3 text-red-600">
                  <MapPin className="h-6 w-6" />
                  <span>Studio Address</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-900 font-semibold mb-1">
                  {businessInfo.name}
                </p>
                <p className="text-gray-700">
                  {businessInfo.address}
                </p>
              </CardContent>
            </Card>

            {/* Phone */}
            <Card className="shadow-lg border-0">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center space-x-3 text-blue-600">
                  <Phone className="h-6 w-6" />
                  <span>Contact Us</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-700 mb-3">
                  Ready to start your martial arts journey?
                </p>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  onClick={handleCall}
                >
                  Call {businessInfo.phone}
                </Button>
              </CardContent>
            </Card>

            {/* Dynamic Training Hours */}
            <Card className="shadow-lg border-0">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center space-x-3 text-green-600">
                  <Clock className="h-6 w-6" />
                  <span>Training Hours</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {trainingHours.map((item, index) => (
                    <div key={index} className="flex justify-between">
                      <span className="font-medium">{item.day}:</span>
                      <span className="text-gray-700">{item.time}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-gray-600">
                    <strong>Note:</strong> All classes by appointment only
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;